import {
  useAdminStats,
  selectLeadVelocity,
  selectPipelineHealth,
  selectTeamLeaderboard,
  DailyActivityEntry,
  StatusBreakdownEntry,
} from './useAdminStats';
import { DeferredData } from '../../../infrastructure/adapters/DeferredDataAdapter';
import { ChartWidgetData } from '../../shared/chart/SharedChartWidget';

// ─────────────────────────────────────────────────────────────────────────────
// Lead Velocity — 14-day acquisition trend
// Normalizes the (sparse) `dailyActivity` payload into a complete, zero-filled
// 14-day window, exactly matching the legacy `daily-chart` renderer in
// public/app.js (loadDashboard): build today-13 … today, then fill counts.
// ─────────────────────────────────────────────────────────────────────────────
function buildLeadVelocity(dailyActivity: DailyActivityEntry[]): ChartWidgetData {
  const days: string[] = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  const counts: Record<string, number> = Object.fromEntries(
    dailyActivity.map((r) => [r.day, r.n])
  );
  return {
    categories: days,
    series: [{ name: 'Leads', data: days.map((d) => counts[d] || 0) }],
  };
}

export function useLeadVelocityAdapter() {
  const { data, isLoading, isError, error, refetch } = useAdminStats(selectLeadVelocity);

  const deferredData: DeferredData<ChartWidgetData> | undefined = data
    ? { loading: false, unavailable: false, error: undefined, data: buildLeadVelocity(data) }
    : undefined;

  return { data: deferredData, isLoading, isError, error, refetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// Pipeline Health — canonical 5-status breakdown
// Always emits the five production pipeline states in canonical order and
// zero-fills any missing status, matching the legacy `ph-container` renderer.
// ─────────────────────────────────────────────────────────────────────────────
const PIPELINE_ORDER = ['new', 'contacted', 'interested', 'converted', 'rejected'] as const;

function buildPipelineHealth(statusBreakdown: StatusBreakdownEntry[]): ChartWidgetData {
  const counts: Record<string, number> = Object.fromEntries(
    statusBreakdown.map((r) => [r.status, r.n])
  );
  return {
    categories: [...PIPELINE_ORDER],
    series: [{ name: 'Count', data: PIPELINE_ORDER.map((s) => counts[s] || 0) }],
  };
}

export function usePipelineHealthAdapter() {
  const { data, isLoading, isError, error, refetch } = useAdminStats(selectPipelineHealth);

  const deferredData: DeferredData<ChartWidgetData> | undefined = data
    ? { loading: false, unavailable: false, error: undefined, data: buildPipelineHealth(data) }
    : undefined;

  return { data: deferredData, isLoading, isError, error, refetch };
}

// ─────────────────────────────────────────────────────────────────────────────
// Team Leaderboard — employee performance ranking
// View-model built from the already-sorted `byEmployee` payload. Rank and the
// inline bar percentage are computed exactly as the legacy table does
// (public/app.js: rank = index, barPct = total_leads / maxLeads). Backend order
// is preserved — no client-side re-sort, re-filter, or aggregation.
// ─────────────────────────────────────────────────────────────────────────────
export interface LeaderboardRow {
  id: string;
  rank: number;
  avatarInitials: string;
  displayName: string;
  username: string;
  role: string;
  active: number;
  totalLeads: number;
  barPct: number;
  leadsToday: number;
  leadsWeek: number;
  contacted: number;
  converted: number;
  totalJobs: number;
  lastActiveLabel: string;
}

// Mirrors the legacy fmtDate() helper in public/app.js.
function fmtDate(s: string | null): string {
  if (!s) return '—';
  try {
    const d = new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
    return d.toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
}

export function useTeamLeaderboardAdapter() {
  const { data, isLoading, isError, error, refetch } = useAdminStats(selectTeamLeaderboard);

  const employees = data || [];
  const maxLeads = Math.max(1, ...employees.map((e) => e.total_leads || 0));

  const rows: LeaderboardRow[] = employees.map((e, idx) => ({
    id: e.id,
    rank: idx + 1,
    avatarInitials: (e.display_name || '?').slice(0, 2).toUpperCase(),
    displayName: e.display_name,
    username: e.username,
    role: e.role,
    active: e.active,
    totalLeads: e.total_leads || 0,
    barPct: Math.round(((e.total_leads || 0) / maxLeads) * 100),
    leadsToday: e.leads_today || 0,
    leadsWeek: e.leads_week || 0,
    contacted: e.contacted || 0,
    converted: e.converted || 0,
    totalJobs: e.total_jobs || 0,
    lastActiveLabel: fmtDate(e.last_active),
  }));

  return { rows, isLoading, isError, error, refetch };
}
